import { useMemo, useState } from "react";
import { useAskSurface } from "@/pages/ask/lib/ask-surface";
import { showQueryInternals } from "@/pages/ask/lib/flags";
import { isCreatorColumn, isVideoIdColumn, LINK_STYLE, normaliseHandle } from "@/pages/ask/lib/tiktok-links";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Row = Record<string, unknown>;

type Props = {
  columns: string[];
  rows: Row[];
  row_count: number;
  execution_ms: number;
  // When true, always render the raw data table — skip metric/chart/list
  // auto-detection. Used in the Sources panel where raw data is most useful.
  forceTable?: boolean;
};

type Shape = "metric_card" | "metric_grid" | "line_chart" | "bar_chart" | "creator_list" | "table";

type LineChartShape = { dateCol: string; valueColumns: string[] };
type BarChartShape = { labelCol: string; valueColumns: string[] };
type AvatarTone = { bg: string; fg: string };

// ---------------------------------------------------------------------------
// Regex / constants
// ---------------------------------------------------------------------------

const DATE_COL_RE = /^(day|date|week|month|period|time|timestamp|dt|ds|created_at)$/i;

const AVATAR_TONES: AvatarTone[] = [
  { bg: "var(--color-brand-100)", fg: "var(--color-brand-700)" },
  { bg: "rgb(209,233,255)", fg: "rgb(23,92,211)" },
  { bg: "rgb(252,231,246)", fg: "rgb(193,21,116)" },
  { bg: "var(--color-gray-200)", fg: "var(--color-gray-700)" },
];

const LINE_COLORS = ["var(--color-brand-700)", "var(--color-success-600)", "#F59E0B", "#EF4444"];
const PLOT = { x0: 48, x1: 692, y0: 20, y1: 178, w: 700, h: 220, xAxisY: 198, labelY: 214 };

// ---------------------------------------------------------------------------
// Column header humaniser
// ---------------------------------------------------------------------------

const COLUMN_LABELS: Record<string, string> = {
  // Creator identity
  username: "Creator",
  creator_id: "Creator",
  creator_name: "Creator",
  handle: "Creator",
  tiktok_handle: "Creator",
  nickname: "Creator",
  follower_count: "Followers",
  follower_tier: "Tier",
  engagement_rate: "Eng. rate",
  engagement_rate_pct: "Eng. rate",
  avg_engagement_rate_pct: "Avg eng. rate",

  // GMV / revenue
  gmv: "GMV",
  gmv_90d: "GMV (90d)",
  gmv_30d: "GMV (30d)",
  creator_gmv_90d: "GMV (90d)",
  creator_gmv_30d: "GMV (30d)",
  total_gmv: "Total GMV",
  lifetime_revenue: "Lifetime GMV",
  revenue: "Revenue",
  peer_gmv: "Peer GMV",
  avg_gmv_per_creator: "Avg GMV",
  avg_gmv_per_video: "GMV / video",
  median_gmv_if_gmv: "Median GMV",
  avg_gmv_if_gmv: "Avg GMV",
  median_gmv_if_converting: "Median GMV",
  gmv_share_pct: "GMV share",

  // Orders / conversion
  total_orders: "Orders",
  order_count: "Orders",
  n_orders: "Orders",
  conversion_rate: "Conv %",
  conversion_rate_pct: "Conv %",
  conv_pct: "Conv %",
  n_got_gmv: "Got GMV",
  revenue_generating: "Rev. gen.",

  // Videos / content
  videos_posted: "Videos",
  video_count: "Videos",
  n_videos: "Videos",
  total_videos: "Videos",
  avg_videos_per_creator: "Avg videos",
  brand_total_videos: "Brand videos",
  brand_total_gmv: "Brand GMV",

  // Views / engagement
  total_views: "Views",
  view_count: "Views",
  total_likes: "Likes",

  // Dates
  first_publish: "First pub.",
  first_publish_date: "First pub.",
  last_publish: "Last pub.",
  last_pub: "Last pub.",
  last_publish_date: "Last pub.",
  publish_date: "Published",
  created_at: "Created",
  updated_at: "Updated",

  // Collaboration / commerce
  commission_rate: "Commission",
  avg_product_price: "Avg price",
  has_free_sample: "Free sample",
  collaboration_type: "Collab type",
  collab_type: "Collab type",

  // Factor splits (diagnostic template)
  bucket: "Bucket",
  factor: "Factor",
  n_got_gmv_count: "Got GMV",
  conv_rate: "Conv %",

  // Merchant
  merchant_id: "Merchant ID",
  merchant_name: "Brand",
  tts_region: "Region",
  vertical: "Vertical",
};

// Acronyms that should stay uppercase
const UPPER_WORDS = new Set(["gmv", "id", "sku", "url", "ctv", "ftc", "aov", "gmv"]);

function humanizeColumnName(col: string): string {
  const lower = col.toLowerCase();
  if (COLUMN_LABELS[lower]) return COLUMN_LABELS[lower];

  return col
    .split("_")
    .map((word, i) => {
      const w = word.toLowerCase();
      if (w === "pct") return "%";
      if (UPPER_WORDS.has(w)) return w.toUpperCase();
      return i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w;
    })
    .join(" ")
    .replace(/\s%$/, "%"); // "Conv %" not "Conv  %"
}

// ---------------------------------------------------------------------------
// Shared utilities
// ---------------------------------------------------------------------------

function isDateColumn(col: string): boolean {
  return (
    DATE_COL_RE.test(col) ||
    col.toLowerCase().includes("date") ||
    col.toLowerCase().includes("week") ||
    col.toLowerCase().includes("month")
  );
}

function isNumericColumn(col: string, rows: Row[]): boolean {
  const samples = rows
    .slice(0, 5)
    .map((r) => r[col])
    .filter((v) => v !== null && v !== undefined);
  return samples.length > 0 && samples.every((v) => !isNaN(parseFloat(String(v))));
}

// ---------------------------------------------------------------------------
// TikTok link helpers
// ---------------------------------------------------------------------------

// renderCell — like formatCell but returns ReactNode so creator/video columns
// become clickable TikTok links. Finds the creator column in the same row when
// building video URLs so both username and video_id are always available.
function renderCell(col: string, value: unknown, row: Row): React.ReactNode {
  const str = formatCell(value);
  if (str === "—") return str;

  if (isCreatorColumn(col)) {
    const handle = normaliseHandle(str);
    if (!handle) return str;
    return (
      <a href={`https://www.tiktok.com/@${handle}`} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>
        {str}
      </a>
    );
  }

  if (isVideoIdColumn(col)) {
    // Find a creator column in the same row to build the full video URL
    const creatorKey = Object.keys(row).find((k) => isCreatorColumn(k));
    const handle = creatorKey ? normaliseHandle(String(row[creatorKey] ?? "")) : "";
    const href = handle ? `https://www.tiktok.com/@${handle}/video/${str}` : `https://www.tiktok.com/video/${str}`;
    // Display the ID truncated — full 19-digit ID is unreadable in a table cell
    const display = str.length > 12 ? `${str.slice(0, 10)}…` : str;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ ...LINK_STYLE, fontFamily: "var(--font-mono)", fontSize: "0.88em", fontWeight: 400 }}
        title={str}
      >
        {display}
      </a>
    );
  }

  return str;
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") {
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  return String(value);
}

function formatDateLabel(val: string): string {
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch {
    return val;
  }
}

function formatYLabel(val: number): string {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(0)}k`;
  return val % 1 === 0 ? String(val) : val.toFixed(1);
}

function niceMax(rawMax: number): number {
  if (rawMax <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(rawMax));
  const candidates = [1, 2, 2.5, 5, 10].map((f) => f * magnitude);
  return candidates.find((c) => c >= rawMax) ?? rawMax * 1.1;
}

function makeInitials(name: string): string {
  const words = String(name)
    .replace(/^@/, "")
    .split(/[\s._-]+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function cycleTone(index: number): AvatarTone {
  return AVATAR_TONES[index % AVATAR_TONES.length];
}

// ---------------------------------------------------------------------------
// Shape detection
// ---------------------------------------------------------------------------

function detectShape(columns: string[], rows: Row[]): Shape {
  // creator_list: any column matching creator pattern
  const hasCreatorCol = columns.some(isCreatorColumn);
  if (hasCreatorCol) return "creator_list";

  const dateCol = columns.find(isDateColumn);
  const numericCols = columns.filter((c) => c !== dateCol && isNumericColumn(c, rows));
  const stringCols = columns.filter((c) => !isDateColumn(c) && !isNumericColumn(c, rows));

  // metric_card or metric_grid: exactly 1 row
  if (rows.length === 1) {
    if (columns.length <= 2) return "metric_card";
    if (columns.length >= 3 && columns.length <= 8) return "metric_grid";
    return "table";
  }

  // line_chart: 2+ rows, has a date col, at least one numeric col
  if (rows.length >= 2 && dateCol && numericCols.length >= 1) {
    return "line_chart";
  }

  // bar_chart: 2–20 rows, exactly 1 string col + 1–2 numeric cols, no date col
  if (
    rows.length >= 2 &&
    rows.length <= 20 &&
    !dateCol &&
    stringCols.length === 1 &&
    numericCols.length >= 1 &&
    numericCols.length <= 2
  ) {
    return "bar_chart";
  }

  return "table";
}

function extractLineChartShape(columns: string[], rows: Row[]): LineChartShape | null {
  const dateCol = columns.find(isDateColumn);
  if (!dateCol) return null;
  const valueColumns = columns.filter((c) => c !== dateCol && isNumericColumn(c, rows));
  if (valueColumns.length === 0) return null;
  return { dateCol, valueColumns };
}

function extractBarChartShape(columns: string[], rows: Row[]): BarChartShape | null {
  const dateCol = columns.find(isDateColumn);
  if (dateCol) return null;
  const numericCols = columns.filter((c) => isNumericColumn(c, rows));
  const stringCols = columns.filter((c) => !isNumericColumn(c, rows));
  if (stringCols.length !== 1 || numericCols.length === 0) return null;
  return { labelCol: stringCols[0], valueColumns: numericCols.slice(0, 2) };
}

// ---------------------------------------------------------------------------
// Toggle bar
// ---------------------------------------------------------------------------

function ViewToggle({
  options,
  active,
  onSelect,
}: {
  options: string[];
  active: string;
  onSelect: (v: string) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: 2,
        background: "var(--color-bg-secondary)",
        borderRadius: 6,
        padding: 2,
        fontFamily: "var(--font-sans)",
      }}
    >
      {options.map((v) => (
        <button
          key={v}
          onClick={() => onSelect(v)}
          style={{
            padding: "2px 10px",
            borderRadius: 4,
            border: "none",
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 500,
            background: active === v ? "var(--color-bg-primary)" : "transparent",
            color: active === v ? "var(--color-text-primary)" : "var(--color-text-quaternary)",
            boxShadow: active === v ? "0 1px 3px rgba(0,0,0,0.10)" : "none",
            transition: "all 0.15s",
            textTransform: "capitalize",
          }}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card wrapper (line + bar charts)
// ---------------------------------------------------------------------------

const CARD_STYLE: React.CSSProperties = {
  background: "var(--color-bg-primary)",
  border: "1px solid var(--color-border-secondary)",
  borderRadius: 12,
  padding: "20px 22px",
  boxShadow: "var(--shadow-xs)",
};

// ---------------------------------------------------------------------------
// MetricGrid
// ---------------------------------------------------------------------------

function MetricGrid({ columns, rows }: { columns: string[]; rows: Row[] }) {
  const row = rows[0];
  const count = columns.length;
  // Hero variant for single column single value
  const isHero = count === 1;

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: `repeat(${Math.min(count, 4)}, 1fr)`,
    gap: 12,
    fontFamily: "var(--font-body)",
  };

  return (
    <div style={gridStyle}>
      {columns.map((col) => (
        <div
          key={col}
          style={{
            background: "var(--color-bg-primary)",
            border: "1px solid var(--color-border-secondary)",
            borderRadius: 12,
            padding: "16px 18px",
            boxShadow: "var(--shadow-xs)",
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "var(--color-text-quaternary)",
            }}
          >
            {humanizeColumnName(col)}
          </div>
          <div
            style={{
              fontSize: isHero ? 30 : 24,
              lineHeight: isHero ? "38px" : "32px",
              fontWeight: 600,
              color: "var(--color-text-primary)",
              marginTop: 4,
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "-0.01em",
            }}
          >
            {formatCell(row[col])}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LineChart
// ---------------------------------------------------------------------------

function ResultsLineChart({ rows, dateCol, valueColumns }: { rows: Row[]; dateCol: string; valueColumns: string[] }) {
  const allVals = rows.flatMap((r) => valueColumns.map((c) => parseFloat(String(r[c] ?? 0))).filter((v) => !isNaN(v)));
  const rawMax = Math.max(...allVals, 0);
  const yMax = niceMax(rawMax);
  const yMin = 0;

  const xStep = (PLOT.x1 - PLOT.x0) / Math.max(rows.length - 1, 1);
  const scaleY = (v: number) => PLOT.y1 - ((v - yMin) / (yMax - yMin)) * (PLOT.y1 - PLOT.y0);

  const yTicks = [0, 1, 2, 3].map((i) => yMin + (yMax - yMin) * (i / 3));

  const xIndexes = (() => {
    const n = rows.length;
    if (n <= 9) return rows.map((_, i) => i);
    const step = Math.ceil(n / 8);
    const idxs: number[] = [];
    for (let i = 0; i < n; i += step) idxs.push(i);
    if (idxs[idxs.length - 1] !== n - 1) idxs.push(n - 1);
    return idxs;
  })();

  return (
    <div style={CARD_STYLE}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-quaternary)" }}>
          {valueColumns.join(" · ")} over time
        </div>
        {valueColumns.length > 1 && (
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            {valueColumns.map((col, i) => (
              <span
                key={col}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  color: "var(--color-text-tertiary)",
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 2,
                    background: LINE_COLORS[i % LINE_COLORS.length],
                    borderRadius: 1,
                    display: "inline-block",
                  }}
                />
                {col}
              </span>
            ))}
          </div>
        )}
      </div>

      <div style={{ overflowX: "auto" }}>
        <svg
          width="100%"
          viewBox={`0 0 ${PLOT.w} ${PLOT.h}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: "block", minWidth: `${PLOT.w}px` }}
        >
          {/* Gridlines */}
          <g stroke="var(--color-border-tertiary)" strokeWidth="1">
            {yTicks.map((_, i) => {
              const y = scaleY(yMin + (yMax - yMin) * (i / 3));
              return <line key={i} x1={PLOT.x0} y1={y} x2={PLOT.x1} y2={y} />;
            })}
          </g>

          {/* Y labels */}
          <g fontFamily="var(--font-mono)" fontSize="10" fill="var(--color-text-quaternary)" textAnchor="end">
            {yTicks.map((v, i) => (
              <text key={i} x={PLOT.x0 - 4} y={scaleY(v) + 4}>
                {formatYLabel(v)}
              </text>
            ))}
          </g>

          {/* Series lines + dots */}
          {valueColumns.map((col, si) => {
            const color = LINE_COLORS[si % LINE_COLORS.length];
            const points = rows
              .map((r, i) => {
                const v = parseFloat(String(r[col] ?? ""));
                if (isNaN(v)) return null;
                return `${PLOT.x0 + i * xStep},${scaleY(v)}`;
              })
              .filter(Boolean)
              .join(" ");

            return (
              <g key={col}>
                <polyline
                  fill="none"
                  stroke={color}
                  strokeWidth={si === 0 ? "2.5" : "2"}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  points={points}
                />
                {rows.length <= 60 &&
                  rows.map((r, i) => {
                    const v = parseFloat(String(r[col] ?? ""));
                    if (isNaN(v)) return null;
                    return (
                      <circle
                        key={i}
                        cx={PLOT.x0 + i * xStep}
                        cy={scaleY(v)}
                        r="3"
                        fill="var(--color-bg-primary)"
                        stroke={color}
                        strokeWidth="2"
                      />
                    );
                  })}
              </g>
            );
          })}

          {/* X axis line */}
          <line
            x1={PLOT.x0}
            y1={PLOT.xAxisY}
            x2={PLOT.x1}
            y2={PLOT.xAxisY}
            stroke="var(--color-border-secondary)"
            strokeWidth="1"
          />

          {/* X labels */}
          <g fontFamily="var(--font-mono)" fontSize="10" fill="var(--color-text-quaternary)" textAnchor="middle">
            {xIndexes.map((i) => (
              <text key={i} x={PLOT.x0 + i * xStep} y={PLOT.labelY}>
                {formatDateLabel(String(rows[i][dateCol] ?? ""))}
              </text>
            ))}
          </g>
        </svg>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BarChart
// ---------------------------------------------------------------------------

const BAR_PLOT = { x0: 60, x1: 700, y0: 30, y1: 180, w: 700, h: 220, xAxisY: 200, labelY: 215 };

function ResultsBarChart({ rows, labelCol, valueColumns }: { rows: Row[]; labelCol: string; valueColumns: string[] }) {
  // Limit to 8 bars
  const displayRows = rows.slice(0, 8);
  const n = displayRows.length;

  const primaryCol = valueColumns[0];
  const allVals = displayRows.map((r) => parseFloat(String(r[primaryCol] ?? 0))).filter((v) => !isNaN(v));
  const rawMax = Math.max(...allVals, 0);
  const yMax = niceMax(rawMax);

  // Grid lines at 4 levels
  const gridYs = [BAR_PLOT.y0, 80, 130, BAR_PLOT.y1];
  const gridVals = gridYs.map((y) => yMax * (1 - (y - BAR_PLOT.y0) / (BAR_PLOT.y1 - BAR_PLOT.y0)));

  const plotW = BAR_PLOT.x1 - BAR_PLOT.x0;
  const slotW = plotW / n;
  const barW = Math.max(20, Math.min(80, slotW * 0.55));

  function barX(i: number) {
    return BAR_PLOT.x0 + i * slotW + (slotW - barW) / 2;
  }
  function barCenterX(i: number) {
    return BAR_PLOT.x0 + i * slotW + slotW / 2;
  }
  function scaleY(v: number) {
    return BAR_PLOT.y1 - (v / yMax) * (BAR_PLOT.y1 - BAR_PLOT.y0);
  }

  function truncateLabel(s: string, maxLen = 10) {
    return s.length > maxLen ? s.slice(0, maxLen - 1) + "…" : s;
  }

  return (
    <div style={CARD_STYLE}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-quaternary)" }}>
          {valueColumns.join(" · ")} by {labelCol}
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <svg
          width="100%"
          viewBox={`0 0 ${BAR_PLOT.w} ${BAR_PLOT.h}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: "block", minWidth: `${BAR_PLOT.w}px` }}
        >
          {/* Gridlines */}
          <g stroke="var(--color-border-tertiary)" strokeWidth="1">
            {gridYs.map((y, i) => (
              <line key={i} x1={BAR_PLOT.x0} y1={y} x2={BAR_PLOT.x1} y2={y} />
            ))}
          </g>

          {/* Y labels */}
          <g fontFamily="var(--font-mono)" fontSize="10" fill="var(--color-text-quaternary)" textAnchor="end">
            {gridYs.map((y, i) => (
              <text key={i} x={BAR_PLOT.x0 - 5} y={y + 4}>
                {formatYLabel(gridVals[i])}
              </text>
            ))}
          </g>

          {/* Bars */}
          <g fill="var(--color-brand-600)">
            {displayRows.map((r, i) => {
              const v = parseFloat(String(r[primaryCol] ?? 0));
              if (isNaN(v) || v <= 0) return null;
              const y = scaleY(v);
              const h = BAR_PLOT.y1 - y;
              return <rect key={i} x={barX(i)} y={y} width={barW} height={h} rx="4" />;
            })}
          </g>

          {/* Value labels above bars */}
          <g
            fontFamily="var(--font-body)"
            fontSize="11"
            fontWeight="600"
            fill="var(--color-text-primary)"
            textAnchor="middle"
          >
            {displayRows.map((r, i) => {
              const v = parseFloat(String(r[primaryCol] ?? 0));
              if (isNaN(v)) return null;
              const y = scaleY(v);
              return (
                <text key={i} x={barCenterX(i)} y={Math.max(y - 4, BAR_PLOT.y0 + 10)}>
                  {formatYLabel(v)}
                </text>
              );
            })}
          </g>

          {/* X axis line */}
          <line
            x1={BAR_PLOT.x0}
            y1={BAR_PLOT.xAxisY}
            x2={BAR_PLOT.x1}
            y2={BAR_PLOT.xAxisY}
            stroke="var(--color-border-secondary)"
            strokeWidth="1"
          />

          {/* X labels */}
          <g fontFamily="var(--font-body)" fontSize="11" fill="var(--color-text-tertiary)" textAnchor="middle">
            {displayRows.map((r, i) => (
              <text key={i} x={barCenterX(i)} y={BAR_PLOT.labelY}>
                {truncateLabel(String(r[labelCol] ?? ""))}
              </text>
            ))}
          </g>
        </svg>
      </div>

      {rows.length > 8 && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: "var(--color-text-quaternary)",
            textAlign: "center",
          }}
        >
          Showing top 8 of {rows.length} rows
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CreatorList
// ---------------------------------------------------------------------------

export function CreatorList({ columns, rows }: { columns: string[]; rows: Row[] }) {
  const [showAll, setShowAll] = useState(false);
  const cap = 20;
  const displayRows = rows.slice(0, showAll ? rows.length : cap);
  const creatorCol = columns.find(isCreatorColumn) ?? columns[0];
  const numericCols = columns.filter((c) => c !== creatorCol && isNumericColumn(c, rows));
  const otherStringCols = columns.filter((c) => c !== creatorCol && !isNumericColumn(c, rows));

  // Grid columns: index | creator | other strings | numerics
  const extraStringCount = otherStringCols.length;

  // In the drawer, columns share the spare width above their minimums so the narrow
  // container fits without scrolling; below the minimums the row scrolls sideways.
  const flexible = useAskSurface() === "drawer";
  const gridCols = (
    flexible
      ? [
          "28px", // rank #
          "minmax(120px, 1.2fr)", // creator
          ...otherStringCols.map(() => "minmax(110px, 1fr)"),
          ...numericCols.map(() => "minmax(56px, auto)"),
        ]
      : [
          "28px", // rank #
          "160px", // creator
          ...otherStringCols.map(() => "120px"),
          ...numericCols.map(() => "90px"),
        ]
  ).join(" ");
  const rowMinWidth = flexible ? "min-content" : "max-content";

  const headerStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: gridCols,
    gap: 12,
    padding: "10px 16px",
    background: "var(--color-bg-secondary)",
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-text-tertiary)",
    borderBottom: "1px solid var(--color-border-secondary)",
    alignItems: "center",
  };

  function rowStyle(isLast: boolean): React.CSSProperties {
    return {
      display: "grid",
      gridTemplateColumns: gridCols,
      gap: 12,
      padding: "12px 16px",
      fontSize: 13,
      alignItems: "center",
      borderBottom: isLast ? "none" : "1px solid var(--color-border-tertiary)",
    };
  }

  // Suppress unused variable warning for extraStringCount
  void extraStringCount;

  return (
    <div
      style={{
        background: "var(--ask-frame-bg, var(--color-bg-primary))",
        border: "1px solid var(--ask-frame-border, var(--color-border-secondary))",
        borderRadius: "var(--ask-frame-radius, 12px)",
        overflow: "hidden",
        fontFamily: "var(--font-body)",
      }}
    >
      {/* Scroll wrapper — allows horizontal scroll when columns exceed container width */}
      <div style={{ overflowX: "auto" }}>
        {/* Header */}
        <div style={{ ...headerStyle, minWidth: rowMinWidth }}>
          <div>#</div>
          <div>{humanizeColumnName(creatorCol)}</div>
          {otherStringCols.map((c) => (
            <div key={c}>{humanizeColumnName(c)}</div>
          ))}
          {numericCols.map((c) => (
            <div key={c} style={{ textAlign: "right" }}>
              {humanizeColumnName(c)}
            </div>
          ))}
        </div>

        {/* Rows */}
        {displayRows.map((row, i) => {
          const tone = cycleTone(i);
          const rawName = String(row[creatorCol] ?? "");
          const initials = makeInitials(rawName);
          const isLast = i === displayRows.length - 1 && (showAll || rows.length <= cap);

          return (
            <div key={i} style={{ ...rowStyle(isLast), minWidth: rowMinWidth }}>
              {/* Rank */}
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--color-text-quaternary)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </div>

              {/* Creator — avatar + name as TikTok profile link */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "9999px",
                    background: tone.bg,
                    color: tone.fg,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 600,
                    border: "1px solid rgba(0,0,0,0.08)",
                    flexShrink: 0,
                  }}
                >
                  {initials}
                </div>
                <a
                  href={`https://www.tiktok.com/@${normaliseHandle(rawName)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 500,
                    color: "var(--color-brand-600)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textDecoration: "none",
                  }}
                >
                  {rawName}
                </a>
              </div>

              {/* Other string columns — renderCell handles video_id links */}
              {otherStringCols.map((c) => (
                <div
                  key={c}
                  style={{
                    color: "var(--color-text-secondary)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {renderCell(c, row[c], row)}
                </div>
              ))}

              {/* Numeric columns — renderCell handles video_id links even if classified numeric */}
              {numericCols.map((c, ni) => (
                <div
                  key={c}
                  style={{
                    textAlign: isVideoIdColumn(c) ? "left" : "right",
                    fontVariantNumeric: "tabular-nums",
                    color: ni === 0 ? "var(--color-text-primary)" : "var(--color-text-tertiary)",
                  }}
                >
                  {renderCell(c, row[c], row)}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {rows.length > cap && (
        <div
          style={{
            padding: "8px 16px",
            fontSize: 12,
            color: "var(--color-text-quaternary)",
            textAlign: "center",
            borderTop: "1px solid var(--color-border-tertiary)",
            background: "var(--color-bg-secondary)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {showAll ? (
            <span>Showing all {rows.length.toLocaleString()} rows</span>
          ) : (
            <span>
              Showing {cap} of {rows.length.toLocaleString()} rows
            </span>
          )}
          <button
            onClick={() => setShowAll((v) => !v)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontSize: 12,
              color: "var(--color-brand-600)",
              fontWeight: 500,
              fontFamily: "inherit",
              textDecoration: "underline",
              textUnderlineOffset: 2,
            }}
          >
            {showAll ? "Show fewer" : `Show all ${rows.length.toLocaleString()} rows`}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table (fallback)
// ---------------------------------------------------------------------------

export function ResultsTableView({ columns, rows }: { columns: string[]; rows: Row[] }) {
  const cap = 200;
  const [showAll, setShowAll] = useState(false);
  const [sort, setSort] = useState<{ col: string; dir: "asc" | "desc" } | null>(null);

  function handleHeaderClick(col: string) {
    setSort((prev) => {
      if (prev === null || prev.col !== col) return { col, dir: "asc" };
      if (prev.dir === "asc") return { col, dir: "desc" };
      return null;
    });
  }

  const sortedRows = useMemo(() => {
    if (sort === null) return rows;
    return [...rows].sort((a, b) => {
      const av = a[sort.col];
      const bv = b[sort.col];
      // Nulls last
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      const an = parseFloat(String(av));
      const bn = parseFloat(String(bv));
      let cmp: number;
      if (isFinite(an) && isFinite(bn)) {
        cmp = an - bn;
      } else {
        cmp = String(av).localeCompare(String(bv));
      }
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [rows, sort]);

  const displayRows = sortedRows.slice(0, showAll ? sortedRows.length : cap);

  // Fixed column widths so the table can overflow horizontally rather than squish
  const colWidth = 140;
  const gridCols = `repeat(${columns.length}, ${colWidth}px)`;

  return (
    <div
      style={{
        border: "1px solid var(--ask-frame-border, var(--color-border-secondary))",
        borderRadius: "var(--ask-frame-radius, 10px)",
        overflow: "hidden",
        background: "var(--ask-frame-bg, var(--color-bg-primary))",
      }}
    >
      {/* Single scroll container covers both header and rows */}
      <div style={{ overflowX: "auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: gridCols,
            padding: "8px 14px",
            background: "var(--color-bg-secondary)",
            borderBottom: "1px solid var(--color-border-secondary)",
            fontSize: 11,
            fontWeight: 500,
            color: "var(--color-text-tertiary)",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            minWidth: "max-content",
          }}
        >
          {columns.map((col) => {
            const isActive = sort?.col === col;
            const ariaSortValue: React.AriaAttributes["aria-sort"] = isActive
              ? sort!.dir === "asc"
                ? "ascending"
                : "descending"
              : "none";
            return (
              <div
                key={col}
                role="columnheader"
                aria-sort={ariaSortValue}
                onClick={() => handleHeaderClick(col)}
                style={{
                  padding: "0 4px",
                  cursor: "pointer",
                  userSelect: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  color: isActive ? "var(--color-text-primary)" : undefined,
                }}
              >
                {humanizeColumnName(col)}
                {isActive && <span style={{ fontSize: 9, lineHeight: 1 }}>{sort!.dir === "asc" ? "▲" : "▼"}</span>}
              </div>
            );
          })}
        </div>

        {displayRows.map((row, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: gridCols,
              padding: "10px 14px",
              fontSize: 13,
              color: "var(--color-text-secondary)",
              borderBottom:
                i === displayRows.length - 1 && (showAll || rows.length <= cap)
                  ? "none"
                  : "1px solid var(--color-border-tertiary)",
              minWidth: "max-content",
            }}
          >
            {columns.map((col) => (
              <div
                key={col}
                style={{
                  padding: "0 4px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 240,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {renderCell(col, row[col], row)}
              </div>
            ))}
          </div>
        ))}
      </div>

      {rows.length > cap && (
        <div
          style={{
            padding: "8px 14px",
            fontSize: 12,
            color: "var(--color-text-quaternary)",
            textAlign: "center",
            borderTop: "1px solid var(--color-border-tertiary)",
            background: "var(--color-bg-secondary)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {showAll ? (
            <span>Showing all {rows.length.toLocaleString()} rows</span>
          ) : (
            <span>
              Showing {cap} of {rows.length.toLocaleString()} rows
            </span>
          )}
          <button
            onClick={() => setShowAll((v) => !v)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontSize: 12,
              color: "var(--color-brand-600)",
              fontWeight: 500,
              fontFamily: "inherit",
              textDecoration: "underline",
              textUnderlineOffset: 2,
            }}
          >
            {showAll ? "Show fewer" : `Show all ${rows.length.toLocaleString()} rows`}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function exportCSV(columns: string[], rows: Row[]) {
  const escapeCsv = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [columns.map(escapeCsv).join(","), ...rows.map((r) => columns.map((c) => escapeCsv(r[c])).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `query-export-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ResultsTable({ columns, rows, row_count, execution_ms, forceTable = false }: Props) {
  const shape = forceTable ? "table" : detectShape(columns, rows);

  // Determine toggle options
  const toggleOptions: string[] | null = (() => {
    if (shape === "line_chart") return ["chart", "table"];
    if (shape === "bar_chart") return ["chart", "table"];
    if (shape === "creator_list") return ["list", "table"];
    return null;
  })();

  const defaultView = (() => {
    if (shape === "line_chart") return "chart";
    if (shape === "bar_chart") return "chart";
    if (shape === "creator_list") return "list";
    if (shape === "metric_card" || shape === "metric_grid") return "metric";
    return "table";
  })();

  const [view, setView] = useState<string>(defaultView);

  if (rows.length === 0) {
    return (
      <p style={{ fontSize: 13, color: "var(--color-text-quaternary)", fontStyle: "italic", marginTop: 8 }}>
        Query returned no rows.
      </p>
    );
  }

  // Derived shapes for chart renderers
  const lineShape = shape === "line_chart" ? extractLineChartShape(columns, rows) : null;
  const barShape = shape === "bar_chart" ? extractBarChartShape(columns, rows) : null;

  return (
    <div style={{ marginTop: 12 }}>
      {/* Meta bar */}
      <div
        style={{
          fontSize: 11,
          color: "var(--color-text-quaternary)",
          marginBottom: 8,
          fontFamily: "var(--font-mono)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        {showQueryInternals && (
          <>
            <span>
              {row_count.toLocaleString()} row{row_count !== 1 ? "s" : ""}
            </span>
            <span style={{ color: "var(--color-border-primary)" }}>·</span>
            <span>{execution_ms}ms</span>
          </>
        )}

        <span style={{ flex: 1 }} />

        <button
          onClick={() => exportCSV(columns, rows)}
          title="Export all rows as CSV"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "2px 9px",
            borderRadius: 5,
            border: "1px solid var(--color-border-secondary)",
            background: "var(--color-bg-primary)",
            color: "var(--color-text-tertiary)",
            fontSize: 11,
            fontWeight: 500,
            fontFamily: "var(--font-sans)",
            cursor: "pointer",
            boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
          }}
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 1v7M3.5 5.5L6 8l2.5-2.5" />
            <path d="M1.5 9.5h9" />
          </svg>
          CSV
        </button>

        {toggleOptions && <ViewToggle options={toggleOptions} active={view} onSelect={setView} />}
      </div>

      {/* Metric card / grid */}
      {(shape === "metric_card" || shape === "metric_grid") && <MetricGrid columns={columns} rows={rows} />}

      {/* Line chart */}
      {shape === "line_chart" && view === "chart" && lineShape && (
        <ResultsLineChart rows={rows} dateCol={lineShape.dateCol} valueColumns={lineShape.valueColumns} />
      )}

      {/* Bar chart */}
      {shape === "bar_chart" && view === "chart" && barShape && (
        <ResultsBarChart rows={rows} labelCol={barShape.labelCol} valueColumns={barShape.valueColumns} />
      )}

      {/* Creator list */}
      {shape === "creator_list" && view === "list" && <CreatorList columns={columns} rows={rows} />}

      {/* Table fallback (or explicit toggle target) */}
      {(shape === "table" || view === "table") && <ResultsTableView columns={columns} rows={rows} />}
    </div>
  );
}
